import { BaseEntityResource } from '@/modules/core/app/resources/BaseEntityResource';
import { RouteGroup } from '@/modules/core/app/constants/api.constants';
import { apiAxios } from '@/modules/core/app/helpers/AxiosHelper';
import { DashboardPage } from '@/modules/ta/dashboard/models/DashboardPage';
import { DashboardRouteSegment } from '@/modules/ta/dashboard/dashboard.constants';

export class DashboardPageResource extends BaseEntityResource {
  constructor() {
    super(RouteGroup.DASH_PAGES, DashboardPage);
  }

  /**
   * @param id
   * @param params
   * @returns {Promise<*>}
   */
  async getForm(id, params = {}) {
    return Promise.all([id ? this.getItem(id, params) : new this.model(), this.getColumns()]);
  }

  /**
   * @param id
   * @param params
   * @returns {Promise<*>}
   */
  async copyItem(id, params = {}) {
    return super.postData(params, `${DashboardRouteSegment.COPY}/${id}`);
  }

  /**
   * @param templateId
   * @param overrides
   * @returns {Promise<*>}
   */
  async createTemplate(templateId, overrides = null) {
    return overrides
      ? apiAxios.post(this._buildRoute(`${RouteGroup.LIBRARY_TEMPLATE}/${templateId}`), overrides)
      : apiAxios.get(this._buildRoute(`${RouteGroup.LIBRARY_TEMPLATE}/${templateId}`));
  }

  /**
   * @param templateId
   * @param value
   * @returns {Promise<*>}
   */
  async toggleTemplate(templateId, value) {
    return apiAxios.post(this._buildRoute(`${templateId}/updateistemplate/${value}`));
  }

  /**
   * @param templateId
   * @param value
   * @returns {Promise<*>}
   */
  async updateIsFavorite(templateId, value) {
    return apiAxios.put(this._buildRoute(`${templateId}/updateisfavorite/${value}`));
  }

  /**
   * @param templateId
   * @param value
   * @returns {Promise<*>}
   */
  async setExecutiveSummaryEnabled(templateId, value) {
    return apiAxios.post(
      this._buildRoute(`${DashboardRouteSegment.ENABLE_SUMMARY}/${templateId}`),
      {
        executive_summary_enabled: value,
      }
    );
  }

  /**
   * @param templateId
   * @returns {Promise<*>}
   */
  async updateThumbnail(templateId, { keepalive = false } = {}) {
    const url = this._buildRoute(`${RouteGroup.LIBRARY}/updatethumbnails/${templateId}`);
    if (keepalive) {
      // Survives page unload, unlike an XHR (used when flushing a pending regen on tab close).
      // eslint-disable-next-line tap/no-raw-text-js -- fetch option, not UI copy
      return fetch(apiAxios.getUri({ url }), { credentials: 'include', keepalive: true });
    }
    // No retries: a retry landing mid-render would queue an extra full re-render.
    return apiAxios.get(url, { 'axios-retry': { retries: 0 } });
  }

  /**
   * Fetch just the thumbnail metadata for a single dashboard (TA-50975).
   *
   * @param {number|string} dashboardId
   * @returns {Promise<{id: number, thumbnail: object|null, has_widgets_on_first_section: boolean|null}>}
   */
  async getThumbnail(dashboardId) {
    return apiAxios.get(this._buildRoute(`${dashboardId}/thumbnail`));
  }

  /**
   * @param dashboardId
   * @returns {Promise<*>}
   */
  async isLeadsDashboard(dashboardId) {
    // eslint-disable-next-line tap/no-raw-text-js
    return apiAxios.get(this._buildRoute(`isleads/${dashboardId}`));
  }

  /**
   * @param q
   * @returns {string}
   */
  addSearchParam(q = null) {
    return `${q}|title`;
  }

  /**
   * @param {number|string} pageId
   * @returns {Promise<{data: Array<object>}>}
   */
  async downloadJson(pageId) {
    const response = await apiAxios.get(this._buildRoute('downloadtojson'), {
      params: { ids: pageId, filters: 1 },
    });
    // apiAxios interceptor strips one envelope; rewrap so the file round-trips through the import endpoint.
    return { data: response };
  }

  /**
   * @param {File} file
   * @param {{dryRun?: boolean}} [options]
   * @returns {Promise<object>}
   */
  async importFromJson(file, { dryRun = false } = {}) {
    const formData = new FormData();
    formData.append('file', file);
    return apiAxios.post(this._buildRoute('importfromjson'), formData, {
      params: dryRun ? { dry_run: 1 } : {},
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }
}

export default new DashboardPageResource();
